import { Button } from "@/components/ui/button";
import { Download, Share2, FileText, Copy } from "lucide-react";
import { CalculationResults } from "@/types/calculator";
import { toast } from "sonner";
import jsPDF from "jspdf";

interface ExportButtonsProps {
  results: CalculationResults;
  inputs: any;
}

const ExportButtons = ({ results, inputs }: ExportButtonsProps) => {
  const generateCSV = () => {
    const headers = ["Week", "Sessions", "Total Minutes", "Calories", "Weight Change (kg)", "Cumulative Change (kg)"];
    const rows = results.weeklyPlan.map(week => [
      week.week,
      week.sessions,
      week.totalMinutes,
      Math.round(week.estimatedCalories),
      week.projectedWeightChange.toFixed(2),
      week.cumulativeWeightChange.toFixed(2),
    ]);

    const csv = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `walking-plan-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success("CSV exported", { description: "Your walking plan has been downloaded" });
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.text("Walking Weight Loss Plan", 20, 20);
    
    // Summary
    doc.setFontSize(12);
    doc.text(`Calories per session: ${Math.round(results.caloriesPerSession)} kcal`, 20, 40);
    doc.text(`Weekly total: ${Math.round(results.weeklyCalories)} kcal`, 20, 50);
    doc.text(`Est. weekly weight loss: ${results.estimatedWeightChange.toFixed(2)} kg`, 20, 60);
    doc.text(`Adjusted TDEE: ${Math.round(results.adjustedTDEE)} kcal/day`, 20, 70);
    
    // Weekly Plan
    doc.setFontSize(14);
    doc.text("12-Week Plan", 20, 90);
    doc.setFontSize(10);
    
    let y = 100;
    results.weeklyPlan.forEach(week => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(
        `Week ${week.week}: ${week.sessions} sessions, ${week.totalMinutes}min, -${week.cumulativeWeightChange.toFixed(2)}kg cumulative`,
        20,
        y
      );
      y += 7;
    });

    // Footer
    doc.setFontSize(8);
    doc.text("Generated by Walking Weight Loss Calculator", 20, 285);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 290);
    
    doc.save(`walking-plan-${new Date().toISOString().split("T")[0]}.pdf`);
    
    toast.success("PDF exported", { description: "Your walking plan has been downloaded" });
  };

  const copyToClipboard = () => {
    const summary = `Walking Weight Loss Plan
    
Calories per session: ${Math.round(results.caloriesPerSession)} kcal
Weekly total: ${Math.round(results.weeklyCalories)} kcal  
Estimated weekly weight loss: ${results.estimatedWeightChange.toFixed(2)} kg
Adjusted TDEE: ${Math.round(results.adjustedTDEE)} kcal/day

12-Week Projection:
${results.weeklyPlan.map(w => 
  `Week ${w.week}: -${w.cumulativeWeightChange.toFixed(2)} kg cumulative`
).join('\n')}

Generated by Walking Weight Loss Calculator`;

    navigator.clipboard.writeText(summary);
    toast.success("Copied!", { description: "Results copied to clipboard" });
  };

  const shareResults = async () => {
    try {
      // Import supabase at function level to avoid bundling issues
      const { supabase } = await import("@/integrations/supabase/client");
      
      // Generate random share code
      const shareCode = Math.random().toString(36).substring(2, 15);
      
      const shareData = {
        results,
        inputs,
        timestamp: Date.now(),
      };
      
      // Store in database
      const { error } = await supabase
        .from("shared_results")
        .insert([{
          share_code: shareCode,
          calculation_data: shareData as any,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        }]);
      
      if (error) {
        console.error("Database error:", error);
        throw error;
      }
      
      const shareUrl = `${window.location.origin}?share=${shareCode}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: "My Walking Weight Loss Plan",
            text: `Check out my personalized walking plan: ${Math.round(results.weeklyCalories)} kcal/week!`,
            url: shareUrl,
          });
          toast.success("Shared successfully!");
        } catch (err) {
          // User cancelled share
          if (err instanceof Error && err.name !== 'AbortError') {
            throw err;
          }
        }
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied!", { 
          description: "Share link copied to clipboard (expires in 7 days)" 
        });
      }
    } catch (error) {
      console.error("Share error:", error);
      toast.error("Failed to create share link", {
        description: "Please try again or export as PDF/CSV instead."
      });
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <Button
        variant="outline"
        size="default"
        onClick={generatePDF}
        className="gap-2 min-w-[44px] min-h-[44px]"
        aria-label="Export as PDF"
      >
        <FileText className="w-4 h-4" />
        <span className="sr-only sm:not-sr-only">PDF</span>
      </Button>

      <Button
        variant="outline"
        size="default"
        onClick={generateCSV}
        className="gap-2 min-w-[44px] min-h-[44px]"
        aria-label="Export as CSV"
      >
        <Download className="w-4 h-4" />
        <span className="sr-only sm:not-sr-only">CSV</span>
      </Button>

      <Button
        variant="outline"
        size="default"
        onClick={copyToClipboard}
        className="gap-2 min-w-[44px] min-h-[44px]"
        aria-label="Copy results to clipboard"
      >
        <Copy className="w-4 h-4" />
        <span className="sr-only sm:not-sr-only">Copy</span>
      </Button>

      <Button
        variant="outline"
        size="default"
        onClick={shareResults}
        className="gap-2 min-w-[44px] min-h-[44px]"
        aria-label="Share results"
      >
        <Share2 className="w-4 h-4" />
        <span className="sr-only sm:not-sr-only">Share</span>
      </Button>
    </div>
  );
};

export default ExportButtons;
